import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import ImageApprovalPanel from '../Admin/ImageApprovalPanel';
import RoleManagement from '../Admin/RoleManagement';
import BookingManagement from '../Admin/BookingManagement';
import TeamManagement from '../Admin/TeamManagement';
import TierManagement from '../Admin/TierManagement';
import PlanConfigPanel from '../Admin/PlanConfigPanel';
import DuplicateCleanupPanel from '../Admin/DuplicateCleanupPanel';
import UserSelector from '../Admin/UserSelector';
import ImageViewRevenuePanel from '../Admin/ImageViewRevenuePanel';
import DataUpdatePanel from '../Debug/DataUpdatePanel';
import CoinsManagement from '../Admin/CoinsManagement';
import CityConfigManager from '../Admin/CityConfigManager';
import CommissionTrackingManager from '../Admin/CommissionTrackingManager';
import ProviderConfigManager from '../Admin/ProviderConfigManager';
import AdminMarketplaceDashboard from '../Marketplace/AdminMarketplaceDashboard';
import VideoCallRecordingManagement from '../Admin/VideoCallRecordingManagement';
import StoreVerificationPanel from '../Admin/StoreVerificationPanel';
import CampaignManagement from '../Admin/CampaignManagement';
import SeedDataGenerator from '../Admin/SeedDataGenerator';
import FeatureConfigPanel from '../NurseApp/Admin/FeatureConfigPanel';

const AdminPanel = () => {
  const { currentUser } = useAuth();
  const [selectedUser, setSelectedUser] = useState(null);
  const [coinAmount, setCoinAmount] = useState(100);
  const [activeTab, setActiveTab] = useState('coins');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleUserSelect = (user) => {
    setSelectedUser(user);
  };

  const handleGiveCoins = async () => {
    if (!selectedUser || !coinAmount) {
      alert('กรุณาเลือกผู้ใช้และใส่จำนวนเหรียญ');
      return;
    }

    setIsSubmitting(true);

    try {
      const userRef = doc(db, 'users', selectedUser.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        alert('ไม่พบผู้ใช้ในระบบ');
        return;
      }

      const currentCoins = userSnap.data().coins || 0;
      const newCoins = currentCoins + parseInt(coinAmount);
      
      await updateDoc(userRef, { coins: newCoins });

      alert(`แจกเหรียญ ${coinAmount} เหรียญให้ ${selectedUser.displayName || selectedUser.nickname || selectedUser.email} สำเร็จ!\nเหรียญรวม: ${newCoins} เหรียญ`);
      
      // Reset form
      setSelectedUser(null);
      setCoinAmount(100);
    } catch (error) {
      console.error('Error giving coins:', error);
      alert('เกิดข้อผิดพลาดในการแจกเหรียญ');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!currentUser || currentUser.role !== 'admin') {
    return <div className="p-6 text-center">❌ คุณไม่มีสิทธิ์เข้าถึงหน้านี้</div>;
  }

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div className="w-64 bg-white shadow-lg">
        <div className="p-6">
          <h1 className="text-xl font-bold text-gray-800 mb-6">แผงควบคุมแอดมิน</h1>
          
          {/* Navigation Menu */}
          <nav className="space-y-2">
            <button
              onClick={() => setActiveTab('coins')}
              className={`w-full text-left py-3 px-4 rounded-lg font-medium transition-colors ${
                activeTab === 'coins'
                  ? 'bg-blue-100 text-blue-600 border-l-4 border-blue-600'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
              }`}
            >
              🪙 จัดการเหรียญ
            </button>
            <button
              onClick={() => setActiveTab('images')}
              className={`w-full text-left py-3 px-4 rounded-lg font-medium transition-colors ${
                activeTab === 'images'
                  ? 'bg-blue-100 text-blue-600 border-l-4 border-blue-600'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
              }`}
            >
              📷 อนุมัติรูปภาพ
            </button>
            <button
              onClick={() => setActiveTab('roles')}
              className={`w-full text-left py-3 px-4 rounded-lg font-medium transition-colors ${
                activeTab === 'roles'
                  ? 'bg-blue-100 text-blue-600 border-l-4 border-blue-600'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
              }`}
            >
              👥 จัดการ Role
            </button>
            <button
              onClick={() => setActiveTab('bookings')}
              className={`w-full text-left py-3 px-4 rounded-lg font-medium transition-colors ${
                activeTab === 'bookings'
                  ? 'bg-blue-100 text-blue-600 border-l-4 border-blue-600'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
              }`}
            >
              📋 จัดการคิว
            </button>
            <button
              onClick={() => setActiveTab('teams')}
              className={`w-full text-left py-3 px-4 rounded-lg font-medium transition-colors ${
                activeTab === 'teams'
                  ? 'bg-blue-100 text-blue-600 border-l-4 border-blue-600'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
              }`}
            >
              🤝 จัดการทีม
            </button>
            <button
              onClick={() => setActiveTab('plans')}
              className={`w-full text-left py-3 px-4 rounded-lg font-medium transition-colors ${
                activeTab === 'plans'
                  ? 'bg-blue-100 text-blue-600 border-l-4 border-blue-600'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
              }`}
            >
              ⭐ จัดการ Plan
            </button>
            <button
              onClick={() => setActiveTab('planConfig')}
              className={`w-full text-left py-3 px-4 rounded-lg font-medium transition-colors ${
                activeTab === 'planConfig'
                  ? 'bg-blue-100 text-blue-600 border-l-4 border-blue-600'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
              }`}
            >
              ⚙️ Config Plan
            </button>
            <button
              onClick={() => setActiveTab('duplicates')}
              className={`w-full text-left py-3 px-4 rounded-lg font-medium transition-colors ${
                activeTab === 'duplicates'
                  ? 'bg-blue-100 text-blue-600 border-l-4 border-blue-600'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
              }`}
            >
              🧹 ทำความสะอาดรูปซ้ำ
            </button>
            <button
              onClick={() => setActiveTab('imageRevenue')}
              className={`w-full text-left py-3 px-4 rounded-lg font-medium transition-colors ${
                activeTab === 'imageRevenue'
                  ? 'bg-blue-100 text-blue-600 border-l-4 border-blue-600'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
              }`}
            >
              💰 รายได้จากรูปภาพ
            </button>
            <button
              onClick={() => setActiveTab('dataUpdate')}
              className={`w-full text-left py-3 px-4 rounded-lg font-medium transition-colors ${
                activeTab === 'dataUpdate'
                  ? 'bg-blue-100 text-blue-600 border-l-4 border-blue-600'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
              }`}
            >
              🔧 จัดการข้อมูล
            </button>
            
            {/* API Providers Integration Section */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                API Providers Integration
              </h3>
              
              <button
                onClick={() => setActiveTab('providerConfig')}
                className={`w-full text-left py-3 px-4 rounded-lg font-medium transition-colors ${
                  activeTab === 'providerConfig'
                    ? 'bg-green-100 text-green-600 border-l-4 border-green-600'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
                }`}
              >
                🔧 จัดการ API Providers
              </button>
              
              <button
                onClick={() => setActiveTab('cityConfig')}
                className={`w-full text-left py-3 px-4 rounded-lg font-medium transition-colors ${
                  activeTab === 'cityConfig'
                    ? 'bg-green-100 text-green-600 border-l-4 border-green-600'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
                }`}
              >
                🏙️ จัดการเมือง
              </button>
              
              <button
                onClick={() => setActiveTab('commissionTracking')}
                className={`w-full text-left py-3 px-4 rounded-lg font-medium transition-colors ${
                  activeTab === 'commissionTracking'
                    ? 'bg-green-100 text-green-600 border-l-4 border-green-600'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
                }`}
              >
                💰 ติดตามรายได้
              </button>
            </div>

            {/* Marketplace Section */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Marketplace
              </h3>
              
              <button
                onClick={() => setActiveTab('marketplace')}
                className={`w-full text-left py-3 px-4 rounded-lg font-medium transition-colors ${
                  activeTab === 'marketplace'
                    ? 'bg-purple-100 text-purple-600 border-l-4 border-purple-600'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
                }`}
              >
                🏪 Marketplace Management
              </button>
              
              <button
                onClick={() => setActiveTab('campaigns')}
                className={`w-full text-left py-3 px-4 rounded-lg font-medium transition-colors ${
                  activeTab === 'campaigns'
                    ? 'bg-purple-100 text-purple-600 border-l-4 border-purple-600'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
                }`}
              >
                📢 จัดการ Campaign
              </button>
              
              <button
                onClick={() => setActiveTab('seedData')}
                className={`w-full text-left py-3 px-4 rounded-lg font-medium transition-colors ${
                  activeTab === 'seedData'
                    ? 'bg-purple-100 text-purple-600 border-l-4 border-purple-600'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
                }`}
              >
                🌱 สร้างข้อมูล Demo
              </button>
            </div>

            {/* NurseApp Section */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                NurseApp
              </h3>
              
              <button
                onClick={() => setActiveTab('featureConfig')}
                className={`w-full text-left py-3 px-4 rounded-lg font-medium transition-colors ${
                  activeTab === 'featureConfig'
                    ? 'bg-indigo-100 text-indigo-600 border-l-4 border-indigo-600'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
                }`}
              >
                ⚙️ Feature Configuration
              </button>
            </div>

            {/* Video Call Section */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Video Call
              </h3>
              
              <button
                onClick={() => setActiveTab('videoCallRecordings')}
                className={`w-full text-left py-3 px-4 rounded-lg font-medium transition-colors ${
                  activeTab === 'videoCallRecordings'
                    ? 'bg-orange-100 text-orange-600 border-l-4 border-orange-600'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
                }`}
              >
                📹 จัดการ Video Call Recordings
              </button>
              
              <button
                onClick={() => setActiveTab('storeVerification')}
                className={`w-full text-left py-3 px-4 rounded-lg font-medium transition-colors ${
                  activeTab === 'storeVerification'
                    ? 'bg-orange-100 text-orange-600 border-l-4 border-orange-600'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
                }`}
              >
                🏪 ตรวจสอบร้านค้า
              </button>
            </div>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-6">
          {/* Tab Content */}
          {activeTab === 'coins' && (
            <CoinsManagement />
          )}

          {activeTab === 'images' && (
            <ImageApprovalPanel />
          )}

          {activeTab === 'roles' && (
            <RoleManagement />
          )}

          {activeTab === 'bookings' && (
            <BookingManagement />
          )}

          {activeTab === 'teams' && (
            <TeamManagement />
          )}

          {activeTab === 'plans' && (
            <TierManagement />
          )}

          {activeTab === 'planConfig' && (
            <PlanConfigPanel />
          )}
          
          {activeTab === 'duplicates' && (
            <DuplicateCleanupPanel />
          )}

          {activeTab === 'imageRevenue' && (
            <ImageViewRevenuePanel />
          )}

          {activeTab === 'dataUpdate' && (
            <DataUpdatePanel />
          )}

          {activeTab === 'providerConfig' && (
            <ProviderConfigManager />
          )}

          {activeTab === 'cityConfig' && (
            <CityConfigManager />
          )}

          {activeTab === 'commissionTracking' && (
            <CommissionTrackingManager />
          )}

          {activeTab === 'marketplace' && (
            <AdminMarketplaceDashboard />
          )}

          {activeTab === 'videoCallRecordings' && (
            <VideoCallRecordingManagement />
          )}

          {activeTab === 'storeVerification' && (
            <StoreVerificationPanel />
          )}

          {activeTab === 'campaigns' && (
            <CampaignManagement />
          )}

          {activeTab === 'seedData' && (
            <SeedDataGenerator />
          )}

          {activeTab === 'featureConfig' && (
            <FeatureConfigPanel />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;